/**
 * 【演示数据 —— 待后端接口就绪后移除】
 *
 * 后端目前只实现了 /api/auth/*：发票、校验、流程、档案、平台运营等接口
 * 正在并行开发（调用会返回 404 / code 40400）。本模块提供与真实契约同形状的
 * 演示数据，让页面在接口就绪前即可完整走查；接口上线后 useResource 会自动
 * 切换到真实数据，删除本目录即可，页面代码无需改动。
 */

import {
  FlowTaskStatus,
  InvoiceStatus,
  InvoiceTypeCode,
  InvoiceValidity,
  RiskLevel,
  RiskItems,
  type ArchiveListItem,
  type AuditLog,
  type CheckReport,
  type Contract,
  type Dept,
  type FlowTask,
  type FlowTaskListQuery,
  type FlowTemplate,
  type InvoiceDetail,
  type InvoiceListItem,
  type InvoiceListQuery,
  type Message,
  type MessageListQuery,
  type OperationLog,
  type OperationLogQuery,
  type PageResult,
  type PlatformMetrics,
  type Plan,
  type ProviderCallLog,
  type ProviderCallLogQuery,
  type ProviderConfig,
  type Role,
  type Tenant,
  type TenantConfig,
  type TenantListItem,
  type TenantListQuery,
  type TenantMetrics,
  type TplusSync,
  type UnreadCountResponse,
  type User,
  type UserListQuery,
} from '@ilc/shared';
import {
  iso,
  makeCheckReport,
  makeInvoice,
  makeInvoiceDetail,
  makeInvoiceListItem,
  mockArchives,
  mockAuditLogs,
  mockContracts,
  mockDepts,
  mockFlowTemplates,
  mockInvoiceId,
  mockMessages,
  mockOperationLogs,
  mockPlans,
  mockPlatformMetrics,
  mockProviderConfigs,
  mockProviderLogs,
  mockRoles,
  mockTenantConfig,
  mockTenantMetrics,
  mockTenants,
  mockUsers,
  type InvoiceSeed,
} from './factories';

export * from './factories';

/* ==========================================================================
 * 发票演示数据（覆盖各状态、各风险等级、各异常分支）
 * ========================================================================== */

const INVOICE_SEEDS: InvoiceSeed[] = [
  {
    id: mockInvoiceId('inv-001'),
    bizNo: 'BZ20260910000101',
    invoiceType: InvoiceTypeCode.VAT_SPECIAL,
    invoiceCode: '3700191130',
    invoiceNo: '24112000000098761234',
    daysAgo: 2,
    sellerName: '济南鲁建建材有限公司',
    sellerTaxNo: '91370100MA3D9K8X2P',
    amountCents: 11_132_743,
    taxCents: 1_447_257,
    invoiceStatus: InvoiceStatus.DRAFT,
    validity: InvoiceValidity.UNKNOWN,
    riskLevel: RiskLevel.NONE,
    riskItems: [],
    contractNo: 'HT-2026-0001',
    projectName: '济南总部办公楼改造',
    remarks: '工程名称：济南市历下区某某项目施工工程',
    handlerName: '孙经办',
  },
  {
    id: mockInvoiceId('inv-002'),
    bizNo: 'BZ20260910000102',
    invoiceType: InvoiceTypeCode.E_INVOICE_SPECIAL,
    invoiceCode: null,
    invoiceNo: '24312000000012349999',
    daysAgo: 2,
    sellerName: '山东顺达物流有限公司',
    sellerTaxNo: '91370100MA3E1F2G3H',
    amountCents: 1_926_605,
    taxCents: 173_395,
    invoiceStatus: InvoiceStatus.CHECKING,
    validity: InvoiceValidity.UNKNOWN,
    riskLevel: RiskLevel.NONE,
    riskItems: [],
    contractNo: 'HT-2026-0002',
    projectName: '济南总部办公楼改造',
    handlerName: '孙经办',
  },
  {
    id: mockInvoiceId('inv-003'),
    bizNo: 'BZ20260908000303',
    invoiceType: InvoiceTypeCode.VAT_SPECIAL,
    invoiceCode: '3700191130',
    invoiceNo: '24112000000098760077',
    daysAgo: 4,
    sellerName: '济南鲁建建材有限公司',
    sellerTaxNo: '91370100MA3D9K8X2P',
    amountCents: 5_309_734,
    taxCents: 690_266,
    invoiceStatus: InvoiceStatus.PENDING_REVIEW,
    validity: InvoiceValidity.RED_FLUSHED,
    riskLevel: RiskLevel.HIGH,
    riskItems: [RiskItems.redFlushed('11223344')],
    contractNo: 'HT-2026-0001',
    projectName: '青岛港区扩建',
    remarks: '工程名称：青岛市黄岛区某某工程；工程地址：长江路 88 号',
    handlerName: '孙经办',
  },
  {
    id: mockInvoiceId('inv-004'),
    bizNo: 'BZ20260908000304',
    invoiceType: InvoiceTypeCode.VAT_SPECIAL,
    invoiceCode: '3700191130',
    invoiceNo: '24112000000098760088',
    daysAgo: 4,
    sellerName: '山东某某贸易有限公司',
    sellerTaxNo: '91370100MA3F9H8J7K',
    amountCents: 2_654_867,
    taxCents: 345_133,
    invoiceStatus: InvoiceStatus.PENDING_REVIEW,
    validity: InvoiceValidity.VALID,
    riskLevel: RiskLevel.MEDIUM,
    riskItems: [
      RiskItems.scopeMismatch(46, ['建筑材料*钢材']),
      RiskItems.checkUnavailable('企查查', '接口请求超时（ETIMEDOUT，已重试 1 次）'),
    ],
    contractNo: 'HT-2026-0001',
    handlerName: '孙经办',
  },
  {
    id: mockInvoiceId('inv-005'),
    bizNo: 'BZ20260905000505',
    invoiceType: InvoiceTypeCode.VAT_GENERAL,
    invoiceCode: '3700191130',
    invoiceNo: '24112000000098760055',
    daysAgo: 6,
    sellerName: '青岛恒信电子有限公司',
    sellerTaxNo: '91370200MA3J4K5L6M',
    amountCents: 872_566,
    taxCents: 113_434,
    invoiceStatus: InvoiceStatus.REJECTED,
    validity: InvoiceValidity.VALID,
    riskLevel: RiskLevel.LOW,
    riskItems: [RiskItems.ocrLowConfidence(['sellerTaxNumber', 'sellerName'], 62)],
    contractNo: 'HT-2026-0003',
    handlerName: '孙经办',
    rejectCount: 1,
    lastRejectReason:
      '供应商名称与合同不一致（合同为「青岛恒信电子有限公司」，票面为「青岛恒信电子」），请核对后补充说明或联系供应商重开。',
  },
  {
    id: mockInvoiceId('inv-006'),
    bizNo: 'BZ20260904000606',
    invoiceType: InvoiceTypeCode.VAT_SPECIAL,
    invoiceCode: '3700191130',
    invoiceNo: '24112000000098760066',
    daysAgo: 8,
    sellerName: '山东顺达物流有限公司',
    sellerTaxNo: '91370100MA3E1F2G3H',
    amountCents: 1_981_651,
    taxCents: 178_349,
    invoiceStatus: InvoiceStatus.APPROVED,
    validity: InvoiceValidity.VALID,
    riskLevel: RiskLevel.LOW,
    riskItems: [RiskItems.contractMissing()],
    contractNo: null,
    handlerName: '孙经办',
  },
  {
    id: mockInvoiceId('inv-007'),
    bizNo: 'BZ20260901000707',
    invoiceType: InvoiceTypeCode.VAT_SPECIAL,
    invoiceCode: '3700191130',
    invoiceNo: '24112000000098760044',
    daysAgo: 12,
    sellerName: '济南鲁建建材有限公司',
    sellerTaxNo: '91370100MA3D9K8X2P',
    amountCents: 4_424_779,
    taxCents: 575_221,
    invoiceStatus: InvoiceStatus.PUSHED,
    validity: InvoiceValidity.VALID,
    riskLevel: RiskLevel.NONE,
    riskItems: [],
    contractNo: 'HT-2026-0001',
    handlerName: '孙经办',
  },
  {
    id: mockInvoiceId('inv-008'),
    bizNo: 'BZ20260820001234',
    invoiceType: InvoiceTypeCode.VAT_SPECIAL,
    invoiceCode: '3700191130',
    invoiceNo: '24112000000098761234',
    daysAgo: 20,
    sellerName: '济南鲁建建材有限公司',
    sellerTaxNo: '91370100MA3D9K8X2P',
    amountCents: 11_132_743,
    taxCents: 1_447_257,
    invoiceStatus: InvoiceStatus.BOOKED,
    validity: InvoiceValidity.VALID,
    riskLevel: RiskLevel.NONE,
    riskItems: [],
    contractNo: 'HT-2026-0001',
    handlerName: '孙经办',
  },
  {
    id: mockInvoiceId('inv-009'),
    bizNo: 'BZ20260818004321',
    invoiceType: InvoiceTypeCode.VAT_SPECIAL,
    invoiceCode: '3700191130',
    invoiceNo: '24112000000098760001',
    daysAgo: 24,
    sellerName: '山东顺达物流有限公司',
    sellerTaxNo: '91370100MA3E1F2G3H',
    amountCents: 1_929_912,
    taxCents: 250_088,
    invoiceStatus: InvoiceStatus.ARCHIVED,
    validity: InvoiceValidity.VALID,
    riskLevel: RiskLevel.MEDIUM,
    riskItems: [RiskItems.partialRed()],
    contractNo: 'HT-2026-0002',
    handlerName: '孙经办',
  },
  {
    id: mockInvoiceId('inv-010'),
    bizNo: 'BZ20260815007788',
    invoiceType: InvoiceTypeCode.E_INVOICE_GENERAL,
    invoiceCode: null,
    invoiceNo: '24312000000012345678',
    daysAgo: 30,
    sellerName: '青岛恒信电子有限公司',
    sellerTaxNo: '91370200MA3J4K5L6M',
    amountCents: 872_566,
    taxCents: 113_434,
    invoiceStatus: InvoiceStatus.ARCHIVED,
    validity: InvoiceValidity.VALID,
    riskLevel: RiskLevel.LOW,
    riskItems: [RiskItems.fieldMissing(['machineCode'])],
    contractNo: 'HT-2026-0003',
    handlerName: '孙经办',
  },
  // —— 外部校验不可用的演示发票（见 factories.ts 的 UNAVAILABLE_CHECK_DEMO）——
  // id 写成合法 UUID：后端对非 UUID 参数直接返回 400（不触发降级），
  // 用合法 UUID 才会得到 404「发票不存在」，从而走本地降级数据看到人工接管通道。
  {
    id: '11111111-1111-4111-8111-111111111101',
    bizNo: 'BZ20260913000991',
    invoiceType: InvoiceTypeCode.VAT_SPECIAL,
    invoiceCode: '3700191130',
    invoiceNo: '24112000000098769911',
    daysAgo: 1,
    sellerName: '山东顺达物流有限公司',
    sellerTaxNo: '91370100MA3E1F2G3H',
    amountCents: 2_000_000,
    taxCents: 260_000,
    invoiceStatus: InvoiceStatus.PENDING_REVIEW,
    validity: InvoiceValidity.UNKNOWN,
    riskLevel: RiskLevel.MEDIUM,
    riskItems: [RiskItems.contractMissing()],
    contractNo: null,
    handlerName: '孙经办',
  },
  {
    id: '11111111-1111-4111-8111-111111111102',
    bizNo: 'BZ20260912000992',
    invoiceType: InvoiceTypeCode.VAT_SPECIAL,
    invoiceCode: '3700191130',
    invoiceNo: '24112000000098769922',
    daysAgo: 2,
    sellerName: '济南鲁建建材有限公司',
    sellerTaxNo: '91370100MA3D9K8X2P',
    amountCents: 3_180_000,
    taxCents: 413_400,
    invoiceStatus: InvoiceStatus.PENDING_REVIEW,
    validity: InvoiceValidity.UNKNOWN,
    riskLevel: RiskLevel.LOW,
    riskItems: [RiskItems.contractMissing()],
    contractNo: 'HT-2026-0001',
    handlerName: '孙经办',
  },
];

const INVOICES = INVOICE_SEEDS.map(makeInvoice);

function paginate<T>(list: T[], page = 1, pageSize = 20): PageResult<T> {
  const start = (page - 1) * pageSize;
  return {
    list: list.slice(start, start + pageSize),
    total: list.length,
    page,
    pageSize,
  };
}

/* ==========================================================================
 * 查询函数（与 endpoints.ts 的方法一一对应）
 * ========================================================================== */

export function mockInvoiceList(query: InvoiceListQuery): PageResult<InvoiceListItem> {
  let list = INVOICES.map((inv) => makeInvoiceListItem(inv));

  if (query.status) list = list.filter((i) => i.invoiceStatus === query.status);
  if (query.riskLevel) list = list.filter((i) => i.riskLevel === query.riskLevel);
  if (query.invoiceType) list = list.filter((i) => i.invoiceType === query.invoiceType);
  if (query.sellerName) list = list.filter((i) => (i.sellerName ?? '').includes(query.sellerName ?? ''));
  if (query.contractNo) list = list.filter((i) => (i.contractNo ?? '').includes(query.contractNo ?? ''));
  if (query.projectName) list = list.filter((i) => (i.projectName ?? '').includes(query.projectName ?? ''));
  if (query.handlerId) list = list.filter((i) => i.handlerId === query.handlerId);
  if (query.keyword) {
    const kw = query.keyword.toLowerCase();
    list = list.filter(
      (i) =>
        i.bizNo.toLowerCase().includes(kw) ||
        i.invoiceNo.toLowerCase().includes(kw) ||
        (i.sellerName ?? '').toLowerCase().includes(kw),
    );
  }
  if (query.dateFrom) list = list.filter((i) => (i.invoiceDate ?? '') >= (query.dateFrom ?? ''));
  if (query.dateTo) list = list.filter((i) => (i.invoiceDate ?? '') <= (query.dateTo ?? ''));

  return paginate(list, query.page, query.pageSize);
}

export function mockInvoiceDetail(id: string): InvoiceDetail {
  const invoice = INVOICES.find((i) => i.id === id) ?? INVOICES[0]!;
  return makeInvoiceDetail(invoice);
}

export function mockCheckReport(id: string): CheckReport {
  const invoice = INVOICES.find((i) => i.id === id) ?? INVOICES[0]!;
  return makeCheckReport(invoice);
}

export function mockFlowTaskList(query: FlowTaskListQuery): PageResult<FlowTask> {
  const all = INVOICES.flatMap((inv) => makeInvoiceDetail(inv).flowInstance?.tasks ?? []);
  let list = all;
  if (query.mine) list = list.filter((t) => t.status === FlowTaskStatus.PENDING);
  if (query.status) list = list.filter((t) => t.status === query.status);
  return paginate(list, query.page, query.pageSize);
}

export function mockUserList(query: UserListQuery): PageResult<User> {
  let list = [...mockUsers];
  if (query.keyword) {
    const kw = query.keyword.toLowerCase();
    list = list.filter((u) => u.username.toLowerCase().includes(kw) || u.name.toLowerCase().includes(kw));
  }
  if (query.deptId) list = list.filter((u) => u.deptId === query.deptId);
  if (query.status) list = list.filter((u) => u.status === query.status);
  if (query.roleId) list = list.filter((u) => u.roles?.some((r) => r.id === query.roleId));
  return paginate(list, query.page, query.pageSize);
}

export function mockRoleList(): Role[] {
  return mockRoles;
}

export function mockDeptList(): Dept[] {
  return mockDepts;
}

export function mockFlowTemplateList(): FlowTemplate[] {
  return mockFlowTemplates;
}

export function mockContractList(query: {
  page?: number;
  pageSize?: number;
  keyword?: string;
}): PageResult<Contract> {
  let list = [...mockContracts];
  if (query.keyword) {
    const kw = query.keyword.toLowerCase();
    list = list.filter(
      (c) =>
        c.contractNo.toLowerCase().includes(kw) ||
        c.name.toLowerCase().includes(kw) ||
        c.supplierName.toLowerCase().includes(kw),
    );
  }
  return paginate(list, query.page, query.pageSize);
}

export function mockArchiveList(query: {
  page?: number;
  pageSize?: number;
  keyword?: string;
  sellerName?: string;
  contractNo?: string;
  booked?: boolean;
}): PageResult<ArchiveListItem> {
  let list = [...mockArchives];
  if (query.keyword) {
    const kw = query.keyword.toLowerCase();
    list = list.filter(
      (a) =>
        a.bizNo.toLowerCase().includes(kw) ||
        a.invoiceNo.toLowerCase().includes(kw) ||
        (a.sellerName ?? '').toLowerCase().includes(kw) ||
        (a.voucherNo ?? '').toLowerCase().includes(kw),
    );
  }
  if (query.sellerName) list = list.filter((a) => (a.sellerName ?? '').includes(query.sellerName ?? ''));
  if (query.contractNo) list = list.filter((a) => (a.contractNo ?? '').includes(query.contractNo ?? ''));
  if (query.booked !== undefined) list = list.filter((a) => (a.voucherNo !== null) === query.booked);
  return paginate(list, query.page, query.pageSize);
}

export function mockMessageList(query: MessageListQuery): PageResult<Message> {
  let list = [...mockMessages];
  if (query.type) list = list.filter((m) => m.type === query.type);
  if (query.isRead !== undefined) list = list.filter((m) => m.isRead === query.isRead);
  return paginate(list, query.page, query.pageSize);
}

export function mockUnreadCount(): UnreadCountResponse {
  return {
    total: mockMessages.filter((m) => !m.isRead).length,
    byType: { TODO: 1, RISK_ALERT: 1 },
  };
}

export function mockOperationLogList(query: OperationLogQuery): PageResult<OperationLog> {
  let list = [...mockOperationLogs];
  if (query.action) list = list.filter((l) => l.action.includes(query.action ?? ''));
  if (query.userId) list = list.filter((l) => l.userId === query.userId);
  return paginate(list, query.page, query.pageSize);
}

export function mockProviderCallList(query: ProviderCallLogQuery): PageResult<ProviderCallLog> {
  let list = [...mockProviderLogs];
  if (query.provider) list = list.filter((l) => l.provider === query.provider);
  if (query.status) list = list.filter((l) => l.status === query.status);
  if (query.invoiceId) list = list.filter((l) => l.invoiceId === query.invoiceId);
  return paginate(list, query.page, query.pageSize);
}

export function mockProviderConfigList(): ProviderConfig[] {
  return mockProviderConfigs;
}

export function mockTenantConfigData(): TenantConfig {
  return mockTenantConfig;
}

export function mockTenantMetricData(): TenantMetrics {
  const counts = INVOICES.reduce<Record<string, number>>((acc, inv) => {
    acc[inv.invoiceStatus] = (acc[inv.invoiceStatus] ?? 0) + 1;
    return acc;
  }, {});
  const full = Object.fromEntries(
    Object.values(InvoiceStatus).map((s) => [s, counts[s] ?? 0]),
  ) as TenantMetrics['invoiceByStatus'];
  return mockTenantMetrics(full);
}

export function mockTenantList(query: TenantListQuery): PageResult<TenantListItem> {
  let list = [...mockTenants];
  if (query.keyword) {
    const kw = query.keyword.toLowerCase();
    list = list.filter(
      (t) => t.name.toLowerCase().includes(kw) || t.code.toLowerCase().includes(kw) || t.creditCode.includes(kw),
    );
  }
  if (query.status) list = list.filter((t) => t.status === query.status);
  if (query.planTier) list = list.filter((t) => t.planTier === query.planTier);
  return paginate(list, query.page, query.pageSize);
}

export function mockTenantDetail(id: string): Tenant {
  const found = mockTenants.find((x) => x.id === id) ?? mockTenants[0]!;
  const {
    planName: _planName,
    planTier: _planTier,
    invoiceCountThisMonth: _invoiceCount,
    quotaHealth: _quotaHealth,
    userCount: _userCount,
    ...tenant
  } = found;
  return tenant;
}

export function mockPlanList(): Plan[] {
  return mockPlans;
}

export function mockPlatformMetricData(): PlatformMetrics {
  return mockPlatformMetrics();
}

export function mockAuditLogList(query: {
  page?: number;
  pageSize?: number;
  category?: string;
  tenantId?: string;
}): PageResult<AuditLog> {
  let list = [...mockAuditLogs];
  if (query.category) list = list.filter((l) => l.category === query.category);
  if (query.tenantId) list = list.filter((l) => l.tenantId === query.tenantId);
  return paginate(list, query.page, query.pageSize);
}

/** 无同步记录时的空壳，避免页面因 null 崩溃 */
export function mockEmptyTplusSync(invoiceId: string): TplusSync {
  return {
    id: `tp-${invoiceId}`,
    tenantId: mockTenantConfig.tenantId,
    createdAt: iso(1),
    updatedAt: iso(1),
    invoiceId,
    status: 'PENDING',
    accountSet: null,
    tplusBillNo: null,
    pushPayload: null,
    pushResponse: null,
    pushedAt: null,
    pushError: null,
    retryCount: 0,
    voucherNo: null,
    bookedAt: null,
    bookingStatus: null,
    callbackPayload: null,
    callbackAt: null,
    // 凭证回写轮询状态（T+ 无凭证类订阅消息，只能轮询）
    voucherPollCount: 0,
    voucherFirstPollAt: null,
    voucherLastPollAt: null,
    voucherPollSeen: null,
    unconfirmedAt: null,
    unconfirmedReason: null,
  };
}
